<script>
    import { Circle2 } from "svelte-loading-spinners";

    import {
        ConfirmActionModal,
        DateInput,
        Loader,
    } from "../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import { createEventDispatcher } from "svelte";

    import { getTitle } from "../../../Utils/utilFunctions.svelte";

    import Select from "svelte-select/Select.svelte";

    import { prePopulateAccountOptions } from "../../../stores";

    import { cacheAccountPrePopulateSearchOptions } from "../../../Utils/utilCache.svelte";

    import { filterSurgeonResults } from "../helpers/encounterHelper.svelte";

    export let isModalOpen = false,
        bulkActionType,
        doctorId,
        selectedPatientEncountersIdArr = [],
        isPatientQuote = false;

    let title,
        totalProgressItemNo,
        isActionRunning = false,
        selectedDate,
        isConfirmActionModalOpen = false,
        failedIdArr = [],
        allSurgeons = [],
        loaded = false,
        isActionFinished = false,
        finishedItemNo = 0,
        selectedHospital = {},
        isLoadingHospitals = false,
        selectedSurgeon = {},
        isLoadingSurgeons = false;

    let selectedDoctor = {},
        selectedDoctorProvidersArr = [],
        selectedDoctorProvider = {},
        isLoadingDoctors = false,
        isLoadingDoctorDetails = false;

    const batchApiCallNumber = 5,
        dispatch = createEventDispatcher();

    $: isProgressFinished = totalProgressItemNo === finishedItemNo;

    $: if (isModalOpen && !loaded) {
        document.onkeydown = null;
        enableCloseDeleteWithESC();
        title = getModalTitle();
        totalProgressItemNo = selectedPatientEncountersIdArr.length;
        if (bulkActionType == "changeSurgeon" || doctorId) {
            loadSurgeonOptions();
        }
        loaded = true;
    }

    const enableCloseDeleteWithESC = () => {
        document.onkeydown = (event) => {
            if (event.key === "Escape") {
                closeModal();
            }
        };
    };

    const getModalTitle = () => {
        switch (bulkActionType) {
            case "archive":
                return `Archive ${isPatientQuote ? "Fee Estimates" : "Encounters"}`;
            case "changeDate":
                return "Change Date";
            case "changeSurgeon":
                return "Change Surgeons";
            case "changeHospital":
                return "Change Hospitals";
            case "changeEncounterDoctor":
                return "Change Doctor";
            default:
                return "";
        }
    };

    const loadSurgeonOptions = async (searchText = "") => {
        isLoadingSurgeons = true;

        await apiMethods.getMethod
            .getAccountDoctors(0, 999999, {
                "filter[doctorId]": doctorId,
                search: searchText,
            })
            .then((res) => {
                if (res.results) {
                    allSurgeons = filterSurgeonResults(res.results).map(
                        (res) => {
                            const { title, firstName, lastName, state } = res;

                            return {
                                ...res,
                                displayName: `${getTitle(title)} ${firstName} ${lastName} ${state ? `(${state})` : ""}`,
                            };
                        },
                    );
                }
            });

        isLoadingSurgeons = false;

        return allSurgeons;
    };

    const loadHospitals = async (searchText) => {
        if (!searchText) return [];

        isLoadingHospitals = true;

        let res = await apiMethods.getMethod.getHospitals(0, 999999, {
            search: searchText,
        });

        isLoadingHospitals = false;

        // return results without restrictions - this action ONLY managed by ADMIN
        return res?.results ?? [];
    };

    $: progressContent = getContent(bulkActionType, isActionFinished);

    const getContent = (bulkActionType, isActionFinished) => {
        if (isActionFinished) {
            return "Action Complete!";
        } else {
            const bulkText = isPatientQuote
                ? "Fee Estimates"
                : "Patient Encounters";
            switch (bulkActionType) {
                case "archive":
                    return `Archive ${bulkText} for the following IDs:`;
                case "changeDate":
                    return `Change Selected ${bulkText} Date for the following IDs:`;
                case "changeSurgeon":
                    return `Change Surgeons for ${bulkText} with following IDs:`;
                case "changeHospital":
                    return `Change Hospitals for ${bulkText} with following IDs:`;
                case "changeEncounterDoctor":
                    return `Change Doctor for ${bulkText} with following IDs:`;
                default:
                    return "";
            }
        }
    };

    const getOptionLabelForSurgeon = ({ firstName, lastName, title }) =>
        `${getTitle(title)} ${firstName} ${lastName}`;

    const closeModal = () => {
        isModalOpen = false;
        document.onkeydown = null;
        bulkActionType = "";
        totalProgressItemNo = 0;
        isActionRunning = false;
        selectedDate = null;
        selectedSurgeon = {};
        isConfirmActionModalOpen = false;
        failedIdArr = [];
        finishedItemNo = 0;
        loaded = false;
        selectedHospital = {};
        selectedDoctor = {};
        selectedDoctorProvidersArr = [];
        selectedDoctorProvider = {};
        if (isActionFinished) dispatch("actionFinished");
        isActionFinished = false;
    };

    const confirmAction = () => {
        switch (bulkActionType) {
            case "archive":
                batchArchive();
                break;
            case "changeDate":
            case "changeSurgeon":
            case "changeHospital":
            case "changeEncounterDoctor":
                batchUpdate(bulkActionType);
                break;
            default:
                break;
        }
    };

    const batchArchive = async () => {
        isActionRunning = true;
        isConfirmActionModalOpen = false;

        finishedItemNo = 0;
        totalProgressItemNo = selectedPatientEncountersIdArr.length;
        while (finishedItemNo < selectedPatientEncountersIdArr.length) {
            let numberOfThisBatch = 0;
            let promises = [];

            while (numberOfThisBatch < batchApiCallNumber) {
                let currentId =
                    selectedPatientEncountersIdArr[
                        finishedItemNo + numberOfThisBatch
                    ];
                if (!currentId) {
                    numberOfThisBatch++;
                    continue;
                }

                promises.push(
                    apiMethods.deleteMethod
                        .deletePatientEncounter(currentId)
                        .then((res) => {
                            if (res.error) {
                                failedIdArr.push({
                                    id: currentId,
                                    error: res.error,
                                });
                            }
                        })
                        .catch((err) => {
                            failedIdArr.push(currentId);
                        }),
                );
                numberOfThisBatch++;
            }
            await Promise.all(promises);
            finishedItemNo += promises.length;
        }

        isActionRunning = false;
        isActionFinished = true;
    };

    const batchUpdate = async (type) => {
        isActionRunning = true;
        isConfirmActionModalOpen = false;

        finishedItemNo = 0;
        totalProgressItemNo = selectedPatientEncountersIdArr.length;

        while (finishedItemNo < selectedPatientEncountersIdArr.length) {
            let numberOfThisBatch = 0;
            let promises = [];

            while (numberOfThisBatch < batchApiCallNumber) {
                let currentId =
                    selectedPatientEncountersIdArr[
                        finishedItemNo + numberOfThisBatch
                    ];
                if (!currentId) {
                    numberOfThisBatch++;
                    continue;
                }

                promises.push(
                    apiMethods.getMethod
                        .getPatientEncounterById(currentId)
                        .then(async (res) => {
                            if (res.id) {
                                let peObj =
                                    type == "changeDate"
                                        ? {
                                              ...res,
                                              scheduledDate:
                                                  selectedDate +
                                                  "T" +
                                                  res.scheduledDate.split(
                                                      "T",
                                                  )[1],
                                          }
                                        : type == "changeSurgeon"
                                          ? {
                                                ...res,
                                                surgeon: {
                                                    id: selectedSurgeon.details
                                                        .id,
                                                    providerUniqueId:
                                                        selectedSurgeon.details
                                                            .uniqueId,
                                                },
                                                surgeonId:
                                                    selectedSurgeon.details.id,
                                            }
                                          : type == "changeHospital"
                                            ? {
                                                  ...res,
                                                  hospital: {
                                                      id: selectedHospital
                                                          .details.id,
                                                  },
                                              }
                                            : type == "changeEncounterDoctor"
                                              ? {
                                                    ...res,
                                                    doctor: {
                                                        ...selectedDoctor.doctorDetail,
                                                    },
                                                    doctorId:
                                                        selectedDoctor
                                                            .doctorDetail.id,
                                                    doctorProvider:
                                                        selectedDoctorProvider.providerDetail
                                                            ? {
                                                                  ...selectedDoctorProvider.providerDetail,
                                                                  number: selectedDoctorProvider
                                                                      .providerDetail
                                                                      .number,
                                                              }
                                                            : {
                                                                  ...res.doctorProvider,
                                                              },
                                                    doctorProviderId:
                                                        selectedDoctorProvider
                                                            .providerDetail.id,
                                                }
                                              : {};

                                await apiMethods.putMethod
                                    .updatePatientEncounter(peObj, false, "Batch update")
                                    .then((res) => {
                                        if (!res.id) {
                                            failedIdArr.push({
                                                id: currentId,
                                                error: res.error,
                                            });
                                        }
                                    })
                                    .catch((err) => {
                                        failedIdArr.push({
                                            id: currentId,
                                            error: err,
                                        });
                                    });
                            } else {
                                failedIdArr.push({
                                    id: currentId,
                                    error: res.error,
                                });
                            }
                        })
                        .catch((err) => {
                            failedIdArr.push({
                                id: currentId,
                                error: err,
                            });
                        }),
                );
                numberOfThisBatch++;
            }
            await Promise.allSettled(promises);
            finishedItemNo += promises.length;
        }

        isActionRunning = false;
        isActionFinished = true;
    };

    // const loadAccountDoctors = () => {
    //     isLoadingAccountDoctors = true;

    //     const obj = {
    //         "filter[doctorId]": isUserAsAdminOrSupport
    //             ? $loggedInDoctorDetail.id
    //             : $user.id,
    //     };

    //     apiMethods.getMethod
    //         .getAccountDoctors(0, 999999, obj)
    //         .then((res) => {
    //             if (res.results) $accountDoctors = [...res.results];
    //         })
    //         .then(() => (isLoadingAccountDoctors = false));
    // };

    const askConfirmArchive = () => (isConfirmActionModalOpen = true);

    const clearCurrentHospital = () => (selectedHospital = {});

    const clearCurrentSurgeon = () => {
        selectedSurgeon = {};
    };

    const clearSelectedDoctor = () => {
        selectedDoctor = {};
        selectedDoctorProvidersArr = [];
        clearSelectedDoctorProvider();
    };

    const clearSelectedDoctorProvider = () => (selectedDoctorProvider = {});

    const loadDoctors = async (filterText) => {
        isLoadingDoctors = true;

        await cacheAccountPrePopulateSearchOptions();

        isLoadingDoctors = false;

        if (!filterText)
            return $prePopulateAccountOptions.length
                ? $prePopulateAccountOptions
                : [];

        return $prePopulateAccountOptions.length
            ? $prePopulateAccountOptions.filter(({ value }) =>
                  value.toLowerCase().includes(filterText.toLowerCase()),
              )
            : [];
    };

    const handleSelectDoctor = async () => {
        isLoadingDoctorDetails = true;

        const {
            doctorDetail: { key: id },
        } = selectedDoctor;

        await apiMethods.getMethod.getAccountById(id).then((res) => {
            selectedDoctor.doctorDetail = {
                ...res,
                key: res.id,
                value: res.fullName,
            };
            selectedDoctorProvidersArr = res.providers;
            if (selectedDoctorProvidersArr.length)
                selectedDoctorProvider.providerDetail = {
                    ...selectedDoctorProvidersArr[0],
                };
        });

        isLoadingDoctorDetails = false;
    };
</script>

<div class={isModalOpen ? "modal modal-open sm:modal-middle" : "modal"}>
    <div
        class="modal-box h-auto max-h-[654px] p-0 flex flex-col no-transform overflow-visible"
    >
        <div
            class="flex flex-0 items-center justify-between font-medium text-lg text-[#fff] bg-[#8a3ed7] p-3 pl-6 rounded-tl-lg rounded-tr-lg"
        >
            <p>{title}</p>

            <button
                class="btn btn-sm btn-circle outline-0 border-0 bg-transparent"
                on:click|preventDefault={closeModal}
            >
                <i class="fa fa-times text-[#fff]" aria-hidden="true" />
            </button>
        </div>

        <div class="flex flex-col gap-y-4 text-[14px] p-[24px]">
            <div class="p-2 leading-6 break-all">
                <p class="text-[15px] font-medium mb-3">
                    {progressContent}
                </p>
                <p class="px-4">{selectedPatientEncountersIdArr.join(", ")}</p>
                {#if isActionFinished && failedIdArr.length}
                    <p class="text-error">
                        Action for the following IDs failed, please try again
                        later or contact support.
                    </p>
                    {#each failedIdArr as failure}
                        <div class="text-error">
                            <span>{failure.id}:&nbsp;</span>
                            <span>{failure.error}</span>
                        </div>
                    {/each}
                {/if}
            </div>
            {#if bulkActionType == "changeDate"}
                <DateInput
                    wrapperClass="input input-sm px-0 input-bordered"
                    wrapperClassFocus="inputFocus"
                    bind:value={selectedDate}
                    isDisable={isActionFinished || isActionRunning}
                />
            {:else if bulkActionType == "changeHospital"}
                <div class="w-full">
                    <Select
                        bind:value={selectedHospital.details}
                        class="flex flex-1 max-h-[35px] rounded-full"
                        loadOptions={loadHospitals}
                        itemId="id"
                        label="name"
                        on:clear={clearCurrentHospital}
                        noOptionsMessage="No hospital found"
                        placeholder="Hospital"
                    />
                </div>
            {:else if bulkActionType == "changeSurgeon"}
                <Select
                    bind:value={selectedSurgeon.details}
                    class="flex flex-1 max-h-[35px] rounded-full"
                    loadOptions={loadSurgeonOptions}
                    itemId="id"
                    label="displayName"
                    on:clear={clearCurrentSurgeon}
                    noOptionsMessage="No surgeons found"
                    placeholder="Surgeons/referrers"
                />
            {:else if bulkActionType == "changeEncounterDoctor"}
                <div class="w-full">
                    <Select
                        bind:value={selectedDoctor.doctorDetail}
                        class="flex flex-1 max-h-[35px] rounded-full"
                        loadOptions={loadDoctors}
                        itemId="key"
                        label="value"
                        on:clear={clearSelectedDoctor}
                        on:select={handleSelectDoctor}
                        noOptionsMessage="No Doctor Found"
                        placeholder="Select Doctor"
                    />
                </div>

                {#if selectedDoctor.doctorDetail}
                    <div class="w-full">
                        {#if isLoadingDoctorDetails}
                            <div class="flex flex-1">
                                <Loader />
                                <p>Loading doctor providers</p>
                            </div>
                        {:else}
                            <Select
                                bind:value={selectedDoctorProvider.providerDetail}
                                id="doctorProviderSelect"
                                class="flex flex-1 max-h-[35px] rounded-full"
                                items={selectedDoctorProvidersArr}
                                itemId="id"
                                label="number"
                                on:clear={clearSelectedDoctorProvider}
                                noOptionsMessage="No Doctor Provider found"
                                placeholder="Select Doctor Provider"
                            />
                        {/if}
                    </div>
                {/if}
            {/if}
        </div>

        <div class="modal-action flex-1 justify-end items-center gap-x-6 p-6">
            {#if isActionRunning || isActionFinished}
                <div class="flex flex-row gap-x-[12px] flex-1">
                    {#if !isProgressFinished}
                        <Circle2 size="32" unit="px" />
                    {/if}
                    <p class="p-2 leading-6 font-semibold">
                        {finishedItemNo - failedIdArr.length} / {totalProgressItemNo}
                        Finished
                    </p>
                    {#if isActionFinished && failedIdArr.length}
                        <p class="text-error p-2 leading-6 font-bold">
                            {failedIdArr.length} Failed
                        </p>
                    {/if}
                </div>
                <button
                    class="w-32 btn-sm btn btn-secondary text-white text-[14px] rounded-md"
                    on:click={closeModal}>ok</button
                >
            {:else if bulkActionType == "archive"}
                <button
                    class="w-auto btn-sm btn btn-error text-white text-[14px] rounded-md"
                    on:click={askConfirmArchive}
                >
                    Archive
                </button>
            {:else if bulkActionType == "changeDate"}
                <button
                    class="w-auto btn-sm btn btn-secondary text-white text-[14px] rounded-md"
                    on:click={askConfirmArchive}
                    disabled={!selectedDate}
                >
                    Update
                </button>
            {:else if bulkActionType == "changeSurgeon"}
                <button
                    class="w-auto btn-sm btn btn-secondary text-white text-[14px] rounded-md"
                    on:click={askConfirmArchive}
                    disabled={!selectedSurgeon}
                >
                    Update
                </button>
            {:else if bulkActionType == "changeHospital"}
                <button
                    class="w-auto btn-sm btn btn-secondary text-white text-[14px] rounded-md"
                    on:click={askConfirmArchive}
                    disabled={!selectedHospital.details}
                >
                    Update
                </button>
            {:else if bulkActionType == "changeEncounterDoctor"}
                <button
                    class="w-auto btn-sm btn btn-secondary text-white text-[14px] rounded-md"
                    on:click={askConfirmArchive}
                    disabled={!selectedDoctor.doctorDetail ||
                        !selectedDoctorProvider.providerDetail}
                >
                    Update
                </button>
            {/if}
        </div>
    </div>
</div>

<ConfirmActionModal
    bind:isConfirmActionModalOpen
    on:confirmAction={() => confirmAction()}
    {title}
    confirmPrompt="Are you sure?"
/>
